"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveAllegroProject = resolveAllegroProject;
exports.resolveExplicitStrokeStyle = resolveExplicitStrokeStyle;
exports.resolveExplicitTextStyle = resolveExplicitTextStyle;
exports.parseQRichText = parseQRichText;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const Transform_1 = require("@cadstrom/sigma-engine-shared/dist/Utils/Transform");
const cssParser_1 = require("../../parsers/css/cssParser");
const AllegroRecordHelpers_1 = require("./AllegroRecordHelpers");
const decode_prop_block_1 = require("../../parsers/ascii/decode/decoders/decode-prop-block");
const decode_tc31_visual_label_1 = require("../../parsers/ascii/decode/decoders/decode-tc31-visual-label");
const AllegroTransformModuleGenerator_1 = require("./Generators/AllegroTransformModuleGenerator");
const DEFAULT_WIRE_WIDTH_MM = 0.1;
const WIRE_STYLE_ROW_INDEX = '1';
const STYLE_LINE_WIDTH_TO_MM = 0.3;
const BUS_WIDTH_MULTIPLIER = 2;
const STYLE_FONT_SIZE_TO_MM = 25.4 / 72;
const DEFAULT_TEXT_SIZE_MM = 1.27;
const JUNCTION_QUANTIZE = 1_000;
const GRID_SPACING = 10;
const GRID_COLUMNS = 8;
function resolveAllegroProject(input) {
    const components = resolveComponents(input);
    const componentByCellName = new Map();
    for (const component of components) {
        componentByCellName.set(component.cellName, component);
    }
    const designs = input.sheetPages.map((page, i) => resolveDesign(page, i, input, componentByCellName));
    const symbolBodyStroke = designs[0]?.wireStroke ?? { width: DEFAULT_WIRE_WIDTH_MM, color: null };
    const projectStyleName = input.styleAssignmentByKey.get(`${designs[0]?.pageNum ?? '1'}:${WIRE_STYLE_ROW_INDEX}`);
    const projectStyle = projectStyleName !== undefined ? input.styles.design.get(projectStyleName) : undefined;
    const instPropTextStyle = textStyleFromProjectStyle(projectStyle, projectStyle?.instPropFontSize ?? null, 'sheet-default');
    const pinPropTextStyle = textStyleFromProjectStyle(projectStyle, projectStyle?.pinPropFontSize ?? null, 'sheet-default');
    return {
        projectName: input.projectName,
        components,
        componentByCellName,
        designs,
        symbolBodyStroke,
        instPropTextStyle,
        pinPropTextStyle,
    };
}
function resolveComponents(input) {
    const components = [];
    const seenCellNames = new Set();
    const xconDesign = input.parsedXcon?.designs[0];
    if (xconDesign !== undefined) {
        const byName = new Map();
        for (const cell of xconDesign.cells.values()) {
            const baseView = resolveComponentView(input, cell.library, cell.name, cell.view, cell.terms) ??
                buildResolvedView(cell.library, cell.view, null, cell.terms, 1, new Map());
            const bucket = byName.get(cell.name);
            if (bucket === undefined) {
                byName.set(cell.name, { primary: cell, baseViews: [baseView] });
            }
            else if (!bucket.baseViews.some((v) => v.view === baseView.view)) {
                bucket.baseViews.push(baseView);
            }
            seenCellNames.add(cell.name);
        }
        for (const { primary, baseViews } of byName.values()) {
            const chips = input.chipsPrtByCellName.get(primary.name.toLowerCase()) ?? null;
            const fanBySection = baseViews.length === 1;
            const sections = fanBySection ? sectionNumbersForCell(primary.name, input) : [1];
            const views = [];
            for (const base of baseViews) {
                for (const section of sections) {
                    const chipsCommaIndex = fanBySection ? section : viewSectionIndex(base.view);
                    const fullMap = buildPinNumberByLogical(primary.name, fanBySection ? section : null, chipsCommaIndex, input, chips);
                    const pinNumberByLogical = restrictToViewTerms(fullMap, base.terms);
                    views.push({ ...base, sectionNumber: section, pinNumberByLogical });
                }
            }
            components.push({
                cellName: primary.name,
                libraryName: primary.library.length > 0 ? primary.library : null,
                xconCellId: primary.id.length > 0 ? primary.id : null,
                views,
                sectionCount: sections.length,
                chips,
                isVirtual: false,
            });
        }
    }
    const seen = seenCellNames;
    const cssOnlyByCell = new Map();
    for (const [key, css] of input.symbolCssByCell) {
        const parts = key.split('__');
        if (parts.length !== 3) {
            continue;
        }
        const [library, name, view] = parts;
        if (seen.has(name)) {
            continue;
        }
        const synthesizedTerms = css.pins.map((p) => ({
            id: '',
            name: p.pinNumber.toLowerCase(),
            direction: 'inout',
        }));
        const resolved = buildResolvedView(library, view, css, synthesizedTerms);
        const bucket = cssOnlyByCell.get(name);
        if (bucket === undefined) {
            cssOnlyByCell.set(name, [resolved]);
        }
        else {
            bucket.push(resolved);
        }
    }
    for (const [name, views] of cssOnlyByCell) {
        components.push({
            cellName: name,
            libraryName: views[0]?.library ?? null,
            xconCellId: null,
            views,
            sectionCount: 1,
            chips: input.chipsPrtByCellName.get(name.toLowerCase()) ?? null,
            isVirtual: true,
        });
    }
    return components;
}
function resolveComponentView(input, library, cellName, view, terms) {
    const direct = input.symbolCssByCell.get(`${library}__${cellName}__${view}`);
    if (direct !== undefined) {
        return buildResolvedView(library, view, direct, terms);
    }
    const suffix = `__${cellName}__${view}`;
    for (const [key, css] of input.symbolCssByCell) {
        if (key.endsWith(suffix)) {
            const fallbackLibrary = key.slice(0, key.length - suffix.length);
            return buildResolvedView(fallbackLibrary, view, css, terms);
        }
    }
    return null;
}
function buildResolvedView(library, view, css, terms, sectionNumber = 1, pinNumberByLogical = new Map()) {
    return {
        library,
        view,
        sectionNumber,
        css,
        pinByLower: css !== null ? buildCssPinLookup(css) : new Map(),
        cssPinRefByOrdinal: css !== null ? css.pins.map((p) => p.pinNumber.toLowerCase()) : [],
        terms,
        pinNumberByLogical,
    };
}
function restrictToViewTerms(full, terms) {
    const allowed = new Set(terms.map((t) => t.name.toLowerCase()));
    const out = new Map();
    for (const [logical, value] of full) {
        if (allowed.has(logical.toLowerCase())) {
            out.set(logical, value);
        }
    }
    return out;
}
function sectionNumbersForCell(cellName, input) {
    const found = new Set();
    for (const xconId of input.cellInstanceIds.get(cellName) ?? []) {
        const sec = input.instanceSectionMap.get(xconId);
        if (sec !== undefined && sec >= 1) {
            found.add(sec);
        }
    }
    if (found.size === 0) {
        return [1];
    }
    return [...found].sort((a, b) => a - b);
}
function buildCssPinLookup(css) {
    const out = new Map();
    for (const pin of css.pins) {
        if (pin.pinNumber.length > 0) {
            out.set(pin.pinNumber.toLowerCase(), pin);
        }
    }
    return out;
}
function buildPinNumberByLogical(cellName, dcfSectionFilter, chipsCommaIndex, input, chips) {
    const out = new Map();
    for (const xconId of input.cellInstanceIds.get(cellName) ?? []) {
        if (dcfSectionFilter !== null) {
            const instanceSection = input.instanceSectionMap.get(xconId) ?? 1;
            if (instanceSection !== dcfSectionFilter) {
                continue;
            }
        }
        const pins = input.instancePinMap.get(xconId);
        if (pins === undefined) {
            continue;
        }
        for (const [logical, pn] of pins) {
            const existing = out.get(logical);
            if (existing === undefined) {
                out.set(logical, pn);
            }
            else if (!existing.split(',').includes(pn)) {
                out.set(logical, `${existing},${pn}`);
            }
        }
    }
    if (chips !== null) {
        for (const [logical, value] of chips.pinNumberByName) {
            if (out.has(logical)) {
                continue;
            }
            if (value.includes(',')) {
                const parts = value.split(',').map((p) => p.trim());
                const selected = parts[chipsCommaIndex - 1] ?? parts[0];
                if (selected !== undefined && selected !== '0' && selected.length > 0) {
                    out.set(logical, selected);
                }
            }
            else if (value.length > 0) {
                out.set(logical, value);
            }
        }
    }
    return out;
}
function viewSectionIndex(view) {
    const m = view.match(/(\d+)$/);
    if (m === null) {
        return 1;
    }
    const n = parseInt(m[1], 10);
    return Number.isFinite(n) && n >= 1 ? n : 1;
}
function resolveDesign(page, pageIndex, input, componentByCellName) {
    const wireStroke = resolveWireStroke(input, page.pageNum);
    const defaultTextStyle = resolveDefaultTextStyle(input, page.pageNum);
    const instances = [];
    let placementSeq = 0;
    for (const record of (0, AllegroRecordHelpers_1.iterateComponentInstances)(page.body)) {
        placementSeq++;
        const resolved = resolveComponentInstance(record, placementSeq, page, input, componentByCellName, wireStroke);
        if (resolved !== null) {
            instances.push(resolved);
        }
    }
    const powerSymbols = [];
    const symbolLabels = [];
    let powerSeq = 0;
    for (const symbol of (0, AllegroRecordHelpers_1.iteratePowerSymbols)(page.body)) {
        powerSeq++;
        const cellName = (0, AllegroRecordHelpers_1.modelNameOf)(symbol.modelRef);
        if ((0, AllegroRecordHelpers_1.isOffpageConnectorModelRef)(symbol.modelRef) || isPortCellName(cellName)) {
            const label = resolveSymbolLabel(symbol, page, input, componentByCellName, wireStroke, defaultTextStyle);
            if (label !== null) {
                symbolLabels.push(label);
            }
            continue;
        }
        const resolved = resolvePowerInstance(symbol, powerSeq, page, input, componentByCellName);
        if (resolved !== null) {
            powerSymbols.push(resolved);
        }
    }
    const wireResult = resolveWires(page, input, wireStroke);
    return {
        pageNum: page.pageNum,
        pageFileName: page.pageFileName,
        sheetName: page.sheetName,
        sheetId: page.sheetId > 0 ? String(page.sheetId) : null,
        instances,
        powerSymbols,
        symbolLabels,
        wires: wireResult.wires,
        buses: wireResult.buses,
        junctions: wireResult.junctions,
        defaultTextStyle,
        wireStroke,
        busStroke: { width: wireStroke.width * BUS_WIDTH_MULTIPLIER, color: wireStroke.color },
        graphics: collectGraphics(page),
        images: collectImages(page),
        richTexts: collectRichTexts(page),
        dashedRects: collectDashedRects(page),
    };
}
const PORT_CELL_NAMES = new Set(['ioport', 'inport', 'outport']);
function isPortCellName(cellName) {
    return PORT_CELL_NAMES.has(cellName.toLowerCase());
}
const GROUND_CELL_NAMES = new Set(['gnd', 'ground']);
function classifyPowerSymbol(cellName, netName) {
    if (GROUND_CELL_NAMES.has(cellName.toLowerCase())) {
        return 'ground';
    }
    if (netName !== null && /(^|_)(GND|GROUND)(_|$)/i.test(netName)) {
        return 'ground';
    }
    return 'power';
}
function resolveComponentInstance(record, placementSeq, page, input, componentByCellName, wireStroke) {
    const cellName = (0, AllegroRecordHelpers_1.modelNameOf)(record.modelRef);
    const view = (0, AllegroRecordHelpers_1.viewOf)(record.modelRef);
    if (componentByCellName.get(cellName) === undefined) {
        return null;
    }
    const xconInstanceId = input.instanceHashMap.get(`${page.pageNum}:${record.placementHash}`) ?? null;
    const gateAttrs = xconInstanceId !== null ? (input.instanceAttributes.get(xconInstanceId) ?? null) : null;
    const dxInstance = xconInstanceId !== null ? (input.parsedDx?.instancesByXconId.get(xconInstanceId) ?? null) : null;
    const dxRefdes = dxInstance?.attributes.refdes;
    const dxProps = dxInstance?.properties;
    const refdes = gateAttrs?.['LOCATION'] ??
        (dxRefdes !== undefined && dxRefdes.length > 0 ? dxRefdes : undefined) ??
        `${page.pageFileName}__${placementSeq}__${cellName}`;
    const moduleRef = `${page.pageFileName}__${placementSeq}__${cellName}`;
    const value = gateAttrs?.['VALUE'] ?? dxProps?.['VALUE'];
    const isDnp = gateAttrs?.['SUB'] === 'NP' || dxProps?.['SUB'] === 'NP';
    const metadata = { ...(dxProps ?? {}), ...(gateAttrs ?? {}) };
    const center = pickCenter(record, placementSeq);
    const transform = (0, AllegroTransformModuleGenerator_1.decodeAllegroTransform)((0, AllegroRecordHelpers_1.parseTransformMatrix)(record.transform), (0, AllegroRecordHelpers_1.parseRotationDeg)(record.rotation), null);
    const component = componentByCellName.get(cellName);
    const dcfSection = xconInstanceId !== null ? (input.instanceSectionMap.get(xconInstanceId) ?? 1) : 1;
    const cssView = component?.views.find((v) => v.view === view && v.sectionNumber === dcfSection) ??
        component?.views.find((v) => v.view === view) ??
        null;
    const sectionNumber = cssView?.sectionNumber ?? dcfSection;
    const instancePins = xconInstanceId !== null ? (input.instancePinMap.get(xconInstanceId) ?? null) : null;
    const pinNetByName = xconInstanceId !== null ? (input.xconPinNetByInstance.get(xconInstanceId) ?? null) : null;
    const terminals = [...(0, AllegroRecordHelpers_1.iterateTerminals)(record)];
    const pins = terminals.map((terminal, idx) => resolvePin(terminal, page, input, {
        cssView,
        component: component ?? null,
        instancePins,
        pinNetByName,
        sectionNumber,
        transform,
        center,
        terminalIndex: idx,
        terminalCount: terminals.length,
    }));
    const explicitBodyStroke = resolveExplicitStrokeStyle(input, page.pageNum, record.styleRowIndex, wireStroke);
    const bodyStroke = explicitBodyStroke.color !== null ? explicitBodyStroke : null;
    return {
        xconInstanceId,
        cellName,
        view,
        refdes,
        moduleRef,
        value,
        center,
        transform,
        metadata,
        isDnp,
        pins,
        sectionNumber,
        styleRowIndex: record.styleRowIndex,
        bodyStroke,
        rawInstance: { kind: 'component', record, placementSeq },
    };
}
function resolvePowerInstance(symbol, placementSeq, page, input, componentByCellName) {
    const cellName = (0, AllegroRecordHelpers_1.modelNameOf)(symbol.modelRef);
    const view = (0, AllegroRecordHelpers_1.viewOf)(symbol.modelRef);
    if (componentByCellName.get(cellName) === undefined) {
        return null;
    }
    const xconInstanceId = input.instanceHashMap.get(`${page.pageNum}:${symbol.placementHash}`) ?? null;
    const center = pickPowerCenter(symbol, placementSeq);
    const transform = (0, AllegroTransformModuleGenerator_1.decodeAllegroTransform)((0, AllegroRecordHelpers_1.parseTransformMatrix)((0, decode_prop_block_1.findPropValue)(symbol.props, 'transform')), (0, AllegroRecordHelpers_1.parseRotationDeg)((0, decode_prop_block_1.findPropValue)(symbol.props, 'rotation')), null);
    const component = componentByCellName.get(cellName);
    const cssView = component?.views.find((v) => v.view === view) ?? null;
    const pinNetByName = xconInstanceId !== null ? (input.xconPinNetByInstance.get(xconInstanceId) ?? null) : null;
    const associatedNetName = resolveByHash(input, page.pageNum, symbol.associatedNetStubId) ??
        resolveTerminalNetFallback(symbol, page.pageNum, input);
    const terminals = [...(0, AllegroRecordHelpers_1.iterateTerminals)(symbol)];
    const pins = terminals.map((terminal, idx) => resolvePin(terminal, page, input, {
        cssView,
        component: component ?? null,
        instancePins: null,
        pinNetByName,
        sectionNumber: 1,
        transform,
        center,
        fallbackNetName: associatedNetName,
        terminalIndex: idx,
        terminalCount: terminals.length,
    }));
    const refdes = symbol.associatedNetStubId !== null ?
        `${cellName}__${symbol.associatedNetStubId}`
        : `${page.pageFileName}__${placementSeq}__${cellName}`;
    const moduleRef = `${page.pageFileName}__${placementSeq}__${cellName}`;
    const signalKind = classifyPowerSymbol(cellName, associatedNetName);
    const signalVoltage = signalKind === 'power' ? parsePowerNetVoltage(associatedNetName, input.netAttributesByName) : undefined;
    return {
        xconInstanceId,
        cellName,
        view,
        refdes,
        moduleRef,
        value: associatedNetName ?? undefined,
        center,
        transform,
        metadata: {},
        isDnp: false,
        pins,
        sectionNumber: 1,
        signalKind,
        signalVoltage,
        styleRowIndex: symbol.styleRowIndex,
        bodyStroke: null,
        rawInstance: { kind: 'power', record: symbol, placementSeq },
    };
}
function parsePowerNetVoltage(netName, netAttributesByName) {
    if (netName === null) {
        return undefined;
    }
    const voltageAttr = netAttributesByName.get(netName)?.['VOLTAGE'];
    if (voltageAttr === undefined || voltageAttr.length === 0) {
        return undefined;
    }
    try {
        return (0, sigma_engine_shared_1.parseNumericalSIValue)(voltageAttr, sigma_engine_shared_1.BoundaryCharacteristicSIDefinitions['voltage']);
    }
    catch {
        return undefined;
    }
}
function resolveSymbolLabel(symbol, page, input, componentByCellName, bodyStroke, textStyle) {
    const xconNetName = resolveByHash(input, page.pageNum, symbol.associatedNetStubId) ??
        resolveTerminalNetFallback(symbol, page.pageNum, input);
    const labelText = xconNetName ?? symbol.associatedNetStubId ?? '';
    if (labelText.length === 0) {
        return null;
    }
    const cellName = (0, AllegroRecordHelpers_1.modelNameOf)(symbol.modelRef);
    const view = (0, AllegroRecordHelpers_1.viewOf)(symbol.modelRef);
    const drawPosition = pickPowerCenter(symbol, 0);
    const termHotpoint = pickFirstTerminalHotpoint(symbol);
    const transform = (0, AllegroTransformModuleGenerator_1.decodeAllegroTransform)((0, AllegroRecordHelpers_1.parseTransformMatrix)((0, decode_prop_block_1.findPropValue)(symbol.props, 'transform')), (0, AllegroRecordHelpers_1.parseRotationDeg)((0, decode_prop_block_1.findPropValue)(symbol.props, 'rotation')), null);
    const cssView = componentByCellName.get(cellName)?.views.find((v) => v.view === view) ?? null;
    const groupDrawings = cssView?.css ? buildSymbolGroupDrawings(cssView.css, transform, bodyStroke, textStyle) : [];
    return {
        cellName,
        view,
        labelText,
        netName: xconNetName,
        combobulatorAnchor: termHotpoint ?? drawPosition,
        drawPosition,
        groupOrigin: drawPosition,
        groupTransform: transform,
        groupDrawings,
    };
}
function buildSymbolGroupDrawings(css, transform, bodyStroke, textStyle) {
    const drawings = [];
    const strokeColor = bodyStroke.color !== null ? { colorOverride: bodyStroke.color } : {};
    const textColor = textStyle.color !== null ? { colorOverride: textStyle.color } : {};
    const textAttr = [];
    if (textStyle.bold) {
        textAttr.push('bold');
    }
    if (textStyle.italic) {
        textAttr.push('italic');
    }
    for (const line of css.lines) {
        drawings.push({
            type: 'segment',
            start: [line.x1 * cssParser_1.CSS_UNIT_TO_MM, -line.y1 * cssParser_1.CSS_UNIT_TO_MM],
            end: [line.x2 * cssParser_1.CSS_UNIT_TO_MM, -line.y2 * cssParser_1.CSS_UNIT_TO_MM],
            width: bodyStroke.width,
            ...strokeColor,
        });
    }
    for (const text of css.texts) {
        if (text.value === undefined || text.value.length === 0) {
            continue;
        }
        drawings.push({
            type: 'text',
            value: text.value,
            pos: [text.x * cssParser_1.CSS_UNIT_TO_MM, -text.y * cssParser_1.CSS_UNIT_TO_MM],
            angle: 0,
            justify: [-1, -1],
            size: textStyle.size,
            thickness: 1,
            ...(textAttr.length > 0 ? { attr: textAttr } : {}),
            ...textColor,
        });
    }
    if (transform.angle === 0 && !transform.mirrored[0] && !transform.mirrored[1]) {
        return drawings;
    }
    return drawings.map((d) => {
        const transformed = (0, Transform_1.transformDrawing)(d, {
            angle: transform.angle,
            mirrored: transform.mirrored,
        });
        if (transformed.type === 'text') {
            return { ...transformed, justify: rotateJustify(transformed.justify, transform) };
        }
        return transformed;
    });
}
function rotateJustify(justify, transform) {
    let [jx, jy] = justify;
    if (transform.mirrored[1]) {
        jx = -jx;
    }
    if (transform.mirrored[0]) {
        jy = -jy;
    }
    const angle = ((transform.angle % 360) + 360) % 360;
    switch (angle) {
        case 0:
            return [jx, jy];
        case 90:
            return [-jy, jx];
        case 180:
            return [-jx, -jy];
        case 270:
            return [jy, -jx];
        default:
            return [jx, jy];
    }
}
function resolvePin(terminal, page, input, opts) {
    const rawTerminalName = (0, AllegroRecordHelpers_1.pinNumberFromTerminal)(terminal);
    const translated = translateDollarPin(rawTerminalName, opts.cssView);
    const logical = translated === null ? '' : translated.toLowerCase();
    const refRaw = physicalPinRef(translated, opts.instancePins, opts.component?.chips ?? null, opts.sectionNumber);
    const ref = opts.component?.isVirtual === true && opts.cssView !== null && refRaw.length > 0 ?
        `${refRaw}@${opts.cssView.view}`
        : refRaw;
    const xconNetName = (logical.length > 0 ? (opts.pinNetByName?.get(logical) ?? null) : null) ??
        resolveByHash(input, page.pageNum, terminal.netHash) ??
        opts.fallbackNetName ??
        null;
    const cssPin = logical.length > 0 ? (opts.cssView?.pinByLower.get(logical) ?? null) : null;
    const symbolLocalPosition = cssPin !== null ? [cssPin.hotX * cssParser_1.CSS_UNIT_TO_MM, -cssPin.hotY * cssParser_1.CSS_UNIT_TO_MM] : null;
    const hotpoint = (0, AllegroRecordHelpers_1.terminalHotpointMm)(terminal);
    const absolutePosition = hotpoint !== null ? [hotpoint.x, hotpoint.y]
        : symbolLocalPosition !== null ? applyPlacementTransform(symbolLocalPosition, opts.transform, opts.center)
            : applyPlacementTransform(placeholderPinLocalPosition(opts.terminalIndex, opts.terminalCount), opts.transform, opts.center);
    return { ref, logical, netName: xconNetName, symbolLocalPosition, absolutePosition };
}
const PLACEHOLDER_HALF_WIDTH = 2.5;
const PLACEHOLDER_PIN_LENGTH = 1;
const PLACEHOLDER_PIN_SPACING = 2.5;
function placeholderPinLocalPosition(termIndex, totalTerms) {
    const sideCount = Math.ceil(Math.max(totalTerms, 1) / 2);
    const onLeftSide = termIndex % 2 === 0;
    const sideIndex = Math.floor(termIndex / 2);
    const verticalSpan = (sideCount - 1) * PLACEHOLDER_PIN_SPACING;
    const y = -verticalSpan / 2 + sideIndex * PLACEHOLDER_PIN_SPACING;
    const x = onLeftSide ? -(PLACEHOLDER_HALF_WIDTH + PLACEHOLDER_PIN_LENGTH) : PLACEHOLDER_HALF_WIDTH + PLACEHOLDER_PIN_LENGTH;
    return [x, y];
}
function translateDollarPin(rawName, cssView) {
    if (rawName === null) {
        return null;
    }
    const match = /^\$pin(\d+)$/i.exec(rawName);
    if (match === null || cssView === null) {
        return rawName;
    }
    const idx = parseInt(match[1], 10);
    const mapped = cssView.cssPinRefByOrdinal[idx];
    return mapped !== undefined && mapped.length > 0 ? mapped : rawName;
}
function physicalPinRef(logicalRaw, instancePins, chips, sectionNumber) {
    if (logicalRaw === null) {
        return '';
    }
    if (instancePins !== null) {
        const pn = instancePins.get(logicalRaw);
        if (pn !== undefined && pn.length > 0) {
            return pn;
        }
    }
    if (chips !== null) {
        const physical = chips.pinNumberByName.get(logicalRaw.toLowerCase());
        if (physical !== undefined && physical.length > 0) {
            if (physical.includes(',')) {
                const parts = physical.split(',').map((p) => p.trim());
                const idx = sectionNumber - 1;
                const selected = parts[idx] ?? parts[0];
                return selected === '0' ? logicalRaw.toLowerCase() : selected;
            }
            return physical;
        }
    }
    return logicalRaw.toLowerCase();
}
function applyPlacementTransform(local, transform, center) {
    const dx = (transform.mirrored[0] ? -1 : 1) * local[0];
    const dy = (transform.mirrored[1] ? -1 : 1) * local[1];
    const rad = (transform.angle * Math.PI) / 180;
    const cosA = Math.cos(rad);
    const sinA = Math.sin(rad);
    return [center[0] + cosA * dx + sinA * dy, center[1] - sinA * dx + cosA * dy];
}
function pickCenter(record, placementSeq) {
    if (record.position !== null) {
        return [record.position.x * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM, record.position.y * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM];
    }
    const bbox = (0, AllegroRecordHelpers_1.bboxToMm)(record.bbox);
    if (bbox !== null && (bbox.x1 !== 0 || bbox.y1 !== 0 || bbox.x2 !== 0 || bbox.y2 !== 0)) {
        return [(bbox.x1 + bbox.x2) / 2, (bbox.y1 + bbox.y2) / 2];
    }
    return gridLayoutCenter(placementSeq);
}
function pickPowerCenter(symbol, placementSeq) {
    if (symbol.position !== null) {
        return [symbol.position.x * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM, symbol.position.y * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM];
    }
    const bbox = (0, AllegroRecordHelpers_1.bboxToMm)(symbol.bbox);
    if (bbox !== null && (bbox.x1 !== 0 || bbox.y1 !== 0 || bbox.x2 !== 0 || bbox.y2 !== 0)) {
        return [(bbox.x1 + bbox.x2) / 2, (bbox.y1 + bbox.y2) / 2];
    }
    return gridLayoutCenter(placementSeq);
}
function gridLayoutCenter(placementSeq) {
    const idx = placementSeq - 1;
    const col = idx % GRID_COLUMNS;
    const row = Math.floor(idx / GRID_COLUMNS);
    return [col * GRID_SPACING, -row * GRID_SPACING];
}
function pickFirstTerminalHotpoint(symbol) {
    for (const terminal of (0, AllegroRecordHelpers_1.iterateTerminals)(symbol)) {
        const hp = (0, AllegroRecordHelpers_1.terminalHotpointMm)(terminal);
        if (hp !== null) {
            return [hp.x, hp.y];
        }
    }
    return null;
}
function resolveByHash(input, pageNum, hash) {
    if (pageNum === null || hash === null) {
        return null;
    }
    const netId = input.netHashMap.get(`${pageNum}:${hash}`);
    if (netId === undefined) {
        return null;
    }
    return resolveNetIdToName(input, netId);
}
function resolveNetIdToName(input, netId) {
    const phys = input.physNetNameByXconId.get(netId);
    if (phys !== undefined) {
        return phys;
    }
    const xconNet = input.parsedXcon?.designs[0]?.nets.get(netId);
    return xconNet?.name ?? null;
}
function resolveTerminalNetFallback(symbol, pageNum, input) {
    for (const terminal of (0, AllegroRecordHelpers_1.iterateTerminals)(symbol)) {
        const name = resolveByHash(input, pageNum, terminal.netHash);
        if (name !== null) {
            return name;
        }
    }
    return null;
}
function resolveWires(page, input, wireStroke) {
    const wires = [];
    const buses = [];
    const endpointCounts = new Map();
    for (const stub of (0, AllegroRecordHelpers_1.iterateNetStubs)(page.body)) {
        if (stub.isDefault) {
            continue;
        }
        const netName = resolveByHash(input, page.pageNum, stub.stubId);
        for (const bundle of (0, AllegroRecordHelpers_1.iterateWireBundles)(stub)) {
            const isBus = bundle.isBus;
            const bundleStroke = resolveBundleStroke(input, page.pageNum, bundle.styleRowIndex, wireStroke);
            for (const segment of (0, AllegroRecordHelpers_1.iterateWireSegments)(bundle)) {
                if (segment.startCoord === null || segment.endCoord === null) {
                    continue;
                }
                const from = [
                    segment.startCoord.x * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
                    segment.startCoord.y * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
                ];
                const to = [
                    segment.endCoord.x * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
                    segment.endCoord.y * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
                ];
                if (isBus) {
                    buses.push({ from, to, stroke: bundleStroke });
                }
                else {
                    wires.push({ from, to, netName, isBus: false, stroke: bundleStroke });
                }
                addEndpoint(endpointCounts, from);
                addEndpoint(endpointCounts, to);
            }
        }
    }
    const junctions = [];
    for (const { x, y, count } of endpointCounts.values()) {
        if (count >= 3) {
            junctions.push({ position: [x, y] });
        }
    }
    return { wires, buses, junctions };
}
function resolveWireStroke(input, pageNum) {
    const styleName = input.styleAssignmentByKey.get(`${pageNum}:${WIRE_STYLE_ROW_INDEX}`);
    const style = styleName !== undefined ? input.styles.design.get(styleName) : undefined;
    return strokeFromStyle(style, 'sheet-default');
}
function resolveBundleStroke(input, pageNum, rowIndex, wireStroke) {
    return resolveExplicitStrokeStyle(input, pageNum, rowIndex, wireStroke);
}
function resolveExplicitStrokeStyle(input, pageNum, rowIndex, fallback) {
    if (rowIndex === null) {
        return fallback;
    }
    const styleName = input.styleAssignmentByKey.get(`${pageNum}:${rowIndex}`);
    if (styleName === undefined) {
        return fallback;
    }
    const style = input.styles.design.get(styleName);
    if (style === undefined) {
        return fallback;
    }
    const width = style.lineWidth !== null && style.lineWidth > 0 ? style.lineWidth * STYLE_LINE_WIDTH_TO_MM : fallback.width;
    const color = style.lineColor !== null ? (hexToColorOverride(style.lineColor) ?? fallback.color) : fallback.color;
    return { width, color };
}
function resolveExplicitTextStyle(input, pageNum, rowIndex, fontSizeOverride, fallback) {
    if (rowIndex === null) {
        return fallback;
    }
    const styleName = input.styleAssignmentByKey.get(`${pageNum}:${rowIndex}`);
    if (styleName === undefined) {
        return fallback;
    }
    const style = input.styles.design.get(styleName);
    if (style === undefined) {
        return fallback;
    }
    const size = fontSizeOverride !== null && fontSizeOverride > 0 ? fontSizeOverride * STYLE_FONT_SIZE_TO_MM
        : style.fontSize !== null && style.fontSize > 0 ? style.fontSize * STYLE_FONT_SIZE_TO_MM
            : fallback.size;
    const color = style.fontColor !== null ? (hexToColorOverride(style.fontColor) ?? fallback.color) : fallback.color;
    return {
        size,
        color,
        font: style.fontName ?? fallback.font,
        italic: style.italic === null ? fallback.italic : style.italic,
        bold: style.bold === null ? fallback.bold : style.bold,
    };
}
function resolveDefaultTextStyle(input, pageNum) {
    const styleName = input.styleAssignmentByKey.get(`${pageNum}:${WIRE_STYLE_ROW_INDEX}`);
    const style = styleName !== undefined ? input.styles.design.get(styleName) : undefined;
    return textStyleFromProjectStyle(style, style?.fontSize ?? null, 'sheet-default');
}
function strokeFromStyle(style, source) {
    const width = style !== undefined && style.lineWidth !== null && style.lineWidth > 0 ?
        style.lineWidth * STYLE_LINE_WIDTH_TO_MM
        : DEFAULT_WIRE_WIDTH_MM;
    const color = source === 'explicit' && style !== undefined ? hexToColorOverride(style.lineColor) : null;
    return { width, color };
}
function textStyleFromProjectStyle(style, fontSizeOverride, source) {
    const fontSize = fontSizeOverride !== null && fontSizeOverride > 0 ? fontSizeOverride
        : style !== undefined && style.fontSize !== null && style.fontSize > 0 ? style.fontSize
            : null;
    const size = fontSize !== null ? fontSize * STYLE_FONT_SIZE_TO_MM : DEFAULT_TEXT_SIZE_MM;
    const color = source === 'explicit' && style !== undefined ? hexToColorOverride(style.fontColor) : null;
    return {
        size,
        color,
        font: style?.fontName ?? null,
        italic: style?.italic === true,
        bold: style?.bold === true,
    };
}
const STYLE_DEFAULT_COLOR = '#000000';
function hexToColorOverride(hex) {
    if (hex === null) {
        return null;
    }
    const trimmed = hex.trim();
    if (trimmed.toLowerCase() === STYLE_DEFAULT_COLOR) {
        return null;
    }
    const m = /^#([0-9a-fA-F]{6})$/.exec(trimmed);
    if (m === null) {
        return null;
    }
    const r = parseInt(m[1].slice(0, 2), 16);
    const g = parseInt(m[1].slice(2, 4), 16);
    const b = parseInt(m[1].slice(4, 6), 16);
    return [r, g, b, 1];
}
function addEndpoint(counts, point) {
    const qx = Math.round(point[0] * JUNCTION_QUANTIZE);
    const qy = Math.round(point[1] * JUNCTION_QUANTIZE);
    const key = `${qx},${qy}`;
    const existing = counts.get(key);
    if (existing === undefined) {
        counts.set(key, { x: qx / JUNCTION_QUANTIZE, y: qy / JUNCTION_QUANTIZE, count: 1 });
    }
    else {
        existing.count++;
    }
}
function collectGraphics(page) {
    const slot = page.body.slots.graphics;
    if (slot === null) {
        return [];
    }
    return [...(0, AllegroRecordHelpers_1.entityItems)(slot, 'graphics-annotation')];
}
function collectRichTexts(page) {
    const slot = page.body.slots.graphics;
    if (slot === null) {
        return [];
    }
    const out = [];
    for (const item of (0, AllegroRecordHelpers_1.entityItems)(slot, 'graphics-annotation')) {
        if (item.subShape !== 'standard-html-annotation') {
            continue;
        }
        const richText = resolveRichTextAnnotation(item);
        if (richText !== null) {
            out.push(richText);
        }
    }
    return out;
}
function resolveRichTextAnnotation(item) {
    const raw = item.raw;
    if (raw.type !== 'container' && raw.type !== 'econtainer') {
        return null;
    }
    const payload = readZeronullHtmlPayload(raw.children);
    if (payload === null || payload.length === 0) {
        return null;
    }
    const extent = readImageExtent(raw.children);
    const positionNode = findPositionContainer(raw.children);
    if (extent === null || positionNode === null) {
        return null;
    }
    const position = readImagePosition(positionNode);
    if (position === null) {
        return null;
    }
    const left = position.x + extent.minX;
    const top = position.y + extent.minY;
    const parsed = parseQRichText(payload);
    if (parsed.paragraphs.length === 0) {
        return null;
    }
    return {
        pos: [left * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM, top * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM],
        width: (extent.maxX - extent.minX) * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
        height: (extent.maxY - extent.minY) * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
        paragraphs: parsed.paragraphs,
        defaultSizePt: parsed.defaultSizePt,
        defaultBold: parsed.defaultBold,
        defaultItalic: parsed.defaultItalic,
        defaultFont: parsed.defaultFont,
    };
}
function readZeronullHtmlPayload(children) {
    for (let i = 0; i + 3 < children.length; i++) {
        const c = children[i];
        const next3 = children[i + 3];
        if (c?.type === 'atom' && c.content === 'zeronull' && next3?.type === 'atom' && typeof next3.content === 'string') {
            return next3.content;
        }
    }
    return null;
}
function parseQRichText(html) {
    const defaults = parseBodyDefaults(html);
    const body = extractBodyInner(html);
    const paragraphs = [];
    const pTagRe = /<p\b([^>]*)>([\s\S]*?)<\/p\s*>/gi;
    let match;
    while ((match = pTagRe.exec(body)) !== null) {
        const inner = match[2];
        const segments = inner.split(/<br\s*\/?\s*>/i);
        for (const segment of segments) {
            const runs = parseRunsFromSegment(segment);
            paragraphs.push({ runs });
        }
    }
    return {
        paragraphs,
        defaultSizePt: defaults.sizePt,
        defaultBold: defaults.bold,
        defaultItalic: defaults.italic,
        defaultFont: defaults.font,
    };
}
function parseRunsFromSegment(segment) {
    const runs = [];
    const tokenRe = /<span\b([^>]*)>([\s\S]*?)<\/span\s*>|([^<]+)/gi;
    let match;
    while ((match = tokenRe.exec(segment)) !== null) {
        if (match[3] !== undefined) {
            const text = decodeEntities(match[3]);
            if (text.length > 0) {
                runs.push({ value: text });
            }
        }
        else {
            const style = parseStyleAttr(match[1]);
            const text = decodeEntities(stripInlineTags(match[2]));
            if (text.length === 0) {
                continue;
            }
            runs.push({
                value: text,
                bold: style.bold,
                italic: style.italic,
                sizePt: style.sizePt,
                font: style.font,
            });
        }
    }
    return runs;
}
function parseBodyDefaults(html) {
    const bodyOpen = /<body\b([^>]*)>/i.exec(html);
    if (bodyOpen === null) {
        return {};
    }
    const styleAttr = /style\s*=\s*("([^"]*)"|'([^']*)')/i.exec(bodyOpen[1]);
    if (styleAttr === null) {
        return {};
    }
    return parseStyleString(styleAttr[2] ?? styleAttr[3] ?? '');
}
function extractBodyInner(html) {
    const m = /<body\b[^>]*>([\s\S]*?)<\/body\s*>/i.exec(html);
    return m === null ? html : m[1];
}
function parseStyleAttr(tagAttrs) {
    const styleAttr = /style\s*=\s*("([^"]*)"|'([^']*)')/i.exec(tagAttrs);
    if (styleAttr === null) {
        return {};
    }
    return parseStyleString(styleAttr[2] ?? styleAttr[3] ?? '');
}
function parseStyleString(style) {
    const out = {};
    for (const decl of style.split(';')) {
        const colon = decl.indexOf(':');
        if (colon < 0) {
            continue;
        }
        const key = decl.slice(0, colon).trim().toLowerCase();
        const value = decl.slice(colon + 1).trim();
        if (key === 'font-size') {
            const ptMatch = /^(\d+(?:\.\d+)?)\s*pt$/i.exec(value);
            if (ptMatch !== null) {
                const n = Number.parseFloat(ptMatch[1]);
                if (Number.isFinite(n) && n > 0) {
                    out.sizePt = n;
                }
            }
        }
        else if (key === 'font-weight') {
            const lower = value.toLowerCase();
            if (lower === 'bold' || lower === 'bolder') {
                out.bold = true;
            }
            else {
                const n = Number.parseInt(lower, 10);
                if (Number.isFinite(n) && n >= 600) {
                    out.bold = true;
                }
            }
        }
        else if (key === 'font-style') {
            const lower = value.toLowerCase();
            if (lower === 'italic' || lower === 'oblique') {
                out.italic = true;
            }
        }
        else if (key === 'font-family') {
            const first = value
                .split(',')[0]
                .trim()
                .replace(/^['"]|['"]$/g, '');
            if (first.length > 0) {
                out.font = first;
            }
        }
    }
    return out;
}
function stripInlineTags(content) {
    return content.replace(/<[^>]*>/g, '');
}
function decodeEntities(text) {
    return text
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ');
}
function collectDashedRects(page) {
    const slot = page.body.slots.graphics;
    if (slot === null) {
        return [];
    }
    const out = [];
    for (const item of (0, AllegroRecordHelpers_1.entityItems)(slot, 'graphics-annotation')) {
        if (item.subShape !== 'generic-graphic-no-cgtype') {
            continue;
        }
        const rect = resolveDashedRect(item);
        if (rect !== null) {
            out.push(rect);
        }
    }
    return out;
}
function resolveDashedRect(item) {
    const raw = item.raw;
    if (raw.type !== 'container' && raw.type !== 'econtainer') {
        return null;
    }
    const extent = readImageExtent(raw.children);
    const positionNode = findPositionContainer(raw.children);
    if (extent === null || positionNode === null) {
        return null;
    }
    const position = readImagePosition(positionNode);
    if (position === null) {
        return null;
    }
    const left = position.x + extent.minX;
    const top = position.y + extent.minY;
    return {
        pos: [left * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM, top * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM],
        width: (extent.maxX - extent.minX) * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
        height: (extent.maxY - extent.minY) * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
    };
}
function collectImages(page) {
    const slot = page.body.slots.graphics;
    if (slot === null) {
        return [];
    }
    const out = [];
    for (const item of (0, AllegroRecordHelpers_1.entityItems)(slot, 'graphics-annotation')) {
        if (item.subShape !== 'image-annotation') {
            continue;
        }
        const image = resolveImagePlacement(item);
        if (image !== null) {
            out.push(image);
        }
    }
    return out;
}
function resolveImagePlacement(item) {
    const filename = (0, decode_prop_block_1.findPropValue)(item.props, 'GRAPHICS_IMAGE_FILE');
    if (filename === null || filename.length === 0) {
        return null;
    }
    const raw = item.raw;
    if (raw.type !== 'container' && raw.type !== 'econtainer') {
        return null;
    }
    const extent = readImageExtent(raw.children);
    const positionNode = findPositionContainer(raw.children);
    if (extent === null || positionNode === null) {
        return null;
    }
    const position = readImagePosition(positionNode);
    if (position === null) {
        return null;
    }
    const angleStr = (0, decode_prop_block_1.findPropValue)(item.props, 'rotation');
    const angle = angleStr !== null ? Number.parseFloat(angleStr) : 0;
    const left = position.x + extent.minX;
    const top = position.y + extent.minY;
    return {
        filename,
        pos: [left * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM, top * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM],
        width: (extent.maxX - extent.minX) * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
        height: (extent.maxY - extent.minY) * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
        angle: Number.isFinite(angle) ? angle : 0,
    };
}
function readImageExtent(children) {
    for (const child of children) {
        if (child.type !== 'container' && child.type !== 'econtainer') {
            continue;
        }
        const inner = child.children;
        if (!Array.isArray(inner)) {
            continue;
        }
        const corners = [];
        for (const grand of inner) {
            const g = grand;
            if (g.type !== 'container' && g.type !== 'econtainer') {
                continue;
            }
            const pos = (0, decode_tc31_visual_label_1.readCadencePosition)(grand);
            if (pos !== null) {
                corners.push(pos);
            }
        }
        if (corners.length >= 2) {
            let minX = corners[0].x;
            let maxX = corners[0].x;
            let minY = corners[0].y;
            let maxY = corners[0].y;
            for (let i = 1; i < corners.length; i++) {
                const c = corners[i];
                if (c.x < minX) {
                    minX = c.x;
                }
                if (c.x > maxX) {
                    maxX = c.x;
                }
                if (c.y < minY) {
                    minY = c.y;
                }
                if (c.y > maxY) {
                    maxY = c.y;
                }
            }
            return { minX, minY, maxX, maxY };
        }
    }
    return null;
}
function findPositionContainer(children) {
    for (const child of children) {
        if (child.type !== 'container' && child.type !== 'econtainer') {
            continue;
        }
        const inner = child.children;
        if (!Array.isArray(inner) || inner.length !== 4) {
            continue;
        }
        if (inner.every((g) => g.type === 'atom')) {
            return child;
        }
    }
    return null;
}
function readImagePosition(node) {
    return (0, decode_tc31_visual_label_1.readCadencePosition)(node);
}
//# sourceMappingURL=AllegroResolveDesign.js.map